import { i18n } from '../../i18n/index.js';
import type { WorkforceAgent } from '../../components/dashboard/AgentStatusWidget.js';
import type { RecentActivityItem } from '../../components/dashboard/RecentActivityWidget.js';
import type { CalendarDeadline } from '../../components/dashboard/CalendarWidget.js';
import type { DashboardKPIStat } from '../../components/dashboard/TodaysStatsWidget.js';
import type { FocusItem } from '../../components/dashboard/FocusTodayWidget.js';
import type { FocusPriority } from '../../components/dashboard/types.js';
import type { NotificationItem } from '../../components/dashboard/NotificationCenterWidget.js';
import type { PipelineItem } from '../../components/dashboard/ActivePipelinesWidget.js';
import type { PipelineBoardCard } from '../../components/dashboard/PipelineBoardWidget.js';
import type { BurndownPoint } from '../../components/dashboard/BurndownWidget.js';
import type { DashboardSignals, DashboardKpiSeries } from './api/dashboard.client.js';
import type { DealListItem } from '../deals/api/deals.client.js';
import type { HotLeadItem } from '../hot-leads/api/hot-leads.client.js';

import type { TaskItem } from '../tasks/api/tasks.client.js';

export interface DashboardViewModel {
  stats: readonly DashboardKPIStat[];
  focusItems: readonly FocusItem[];
  workforce: readonly WorkforceAgent[];
  burndownData: readonly BurndownPoint[];
  pipelines: readonly PipelineItem[];
  pipelineCards: readonly PipelineBoardCard[];
  activityItems: readonly RecentActivityItem[];
  deadlines: readonly CalendarDeadline[];
  notifications: readonly NotificationItem[];
  onboarding: DashboardOnboardingState;
}

/** Internal entity handle some producers store as the activity subject. */
const ENTITY_HANDLE_PATTERN = /^crm:[A-Za-z0-9_-]+$/;

import type {
  DashboardStats,
  DashboardActivity,
  DashboardOnboardingState,
} from './api/dashboard.client.js';

export interface BuildDashboardViewModelInput {
  deals: readonly DealListItem[];
  hotLeads: readonly HotLeadItem[];
  tasks?: readonly TaskItem[];
  signals: DashboardSignals;
  stats: DashboardStats;
  activities: readonly DashboardActivity[];
  onboarding: DashboardOnboardingState;
  /** Daily KPI trend series (server aggregate); absent on API failure. */
  kpiSeries?: DashboardKpiSeries;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function translateHotLeadReason(
  reason: HotLeadItem['primaryReason'],
  t: BuildDashboardViewModelInput['t'],
): string {
  return t(`crm.hot_leads.reason.${reason}`);
}

export function buildDashboardViewModel({
  deals,
  hotLeads,
  tasks,
  signals,
  stats: apiStats,
  activities,
  onboarding,
  kpiSeries,
  t,
}: BuildDashboardViewModelInput): DashboardViewModel {
  const activeDealsCount = deals.filter(
    (deal) => deal.stage !== 'Won' && deal.stage !== 'Lost',
  ).length;
  const wonDealsCount = deals.filter((deal) => deal.stage === 'Won').length;
  const lostDealsCount = deals.filter((deal) => deal.stage === 'Lost').length;
  const closedDealsCount = wonDealsCount + lostDealsCount;
  const successRate =
    closedDealsCount > 0 ? Math.round((wonDealsCount / closedDealsCount) * 100) : 0;
  const wonRevenue = deals
    .filter((deal) => deal.stage === 'Won')
    .reduce((total, deal) => total + Number(deal.amount), 0);

  // ── KPI trends (spec D: real delta vs previous period + sparkline) ───────
  // The series arrive as 60 daily buckets, so every window (30-vs-prev-30,
  // 7-vs-prev-7) is derived from the same aggregate.
  const windowSum = (
    points: readonly { value: number }[],
    lastDays: number,
    offsetDays = 0,
  ): number => {
    const end = points.length - offsetDays;
    const start = Math.max(0, end - lastDays);
    return points.slice(start, end).reduce((sum, point) => sum + point.value, 0);
  };
  const percentDelta = (points: readonly { value: number }[], days: number): number | undefined => {
    const current = windowSum(points, days);
    const previous = windowSum(points, days, days);
    if (previous === 0) return undefined; // no honest baseline → no delta
    return Math.round(((current - previous) / previous) * 1000) / 10;
  };

  const newDealsPoints = kpiSeries?.newDealsByDay ?? [];
  const newContactsPoints = kpiSeries?.newContactsByDay ?? [];
  const revenuePoints = kpiSeries?.revenueByDay ?? [];
  const last30 = (points: readonly { value: number }[]): number[] =>
    points.slice(-30).map((point) => point.value);
  const dealsDelta = percentDelta(newDealsPoints, 30);
  const contactsDelta = percentDelta(newContactsPoints, 7);
  const revenueDelta = percentDelta(revenuePoints, 30);

  const stats: readonly DashboardKPIStat[] = [
    {
      key: 'projects',
      label: i18n.t('crm.features.features.dashboard.dashboard.vm.онлайн_записи'),
      value: apiStats.dealsTotal > 0 ? apiStats.dealsTotal : deals.length,
      color: 'var(--color-primary, #c47322)',
      ...(dealsDelta !== undefined ? { trend: dealsDelta } : {}),
      noteKey: 'crm.dashboard.kpi.vs_last_period',
      ...(newDealsPoints.length > 1 ? { spark: last30(newDealsPoints) } : {}),
    },
    {
      key: 'active',
      label: i18n.t('crm.features.features.dashboard.dashboard.vm.свободные_окна'),
      value: activeDealsCount,
      color: 'var(--color-success, #16a34a)',
      // No honest time series for the live count; the delta is the real number
      // of deals created in the window (absolute, like the prototype's
      // "6 не подтверждены").
      trendKind: 'count',
      trend: windowSum(newDealsPoints, 30),
      noteKey: 'crm.dashboard.kpi.created_recently',
    },
    {
      key: 'tasks-done',
      label: i18n.t('crm.features.features.dashboard.dashboard.vm.новые_клиенты'),
      value: apiStats.hotLeadsCount > 0 ? apiStats.hotLeadsCount : hotLeads.length,
      color: 'var(--color-info, #2563eb)',
      ...(contactsDelta !== undefined ? { trend: contactsDelta } : {}),
      noteKey: 'crm.dashboard.kpi.this_week',
      ...(newContactsPoints.length > 1 ? { spark: last30(newContactsPoints) } : {}),
    },
    {
      key: 'success-rate',
      label: i18n.t('crm.features.features.dashboard.dashboard.vm.возвратность'),
      value: `${successRate}%`,
      color: 'var(--color-info, #2563eb)',
    },
    {
      key: 'sessions',
      label: i18n.t('crm.features.features.dashboard.dashboard.vm.отмены'),
      value: lostDealsCount,
      color: 'var(--color-error, #b91c1c)',
    },
    {
      key: 'total-cost',
      label: i18n.t('crm.features.features.dashboard.dashboard.vm.выручка_смена'),
      value: wonRevenue,
      color: 'var(--color-warning, #d97706)',
      suffix: '₽',
      ...(revenueDelta !== undefined ? { trend: revenueDelta } : {}),
      noteKey: 'crm.dashboard.kpi.vs_last_period',
      ...(revenuePoints.length > 1 ? { spark: last30(revenuePoints) } : {}),
    },
  ];

  const nowTime = Date.now();
  // Deal list join: hot-lead focus rows take the amount and the last-contact
  // timestamp from the matching deal (the hot-lead projection has no dates).
  const dealById = new Map(deals.map((deal) => [deal.id, deal]));
  const focusItems: readonly FocusItem[] = [
    ...signals.pendingApprovals.map((approval) => ({
      id: `approval-${approval.id}`,
      title: t('crm.dashboard.focus.pending_approval_title'),
      subtitle: t('crm.dashboard.focus.pending_approval_subtitle'),
      status: 'pending' as const,
      priority: 'P0' as const,
      actionLabel: t('crm.dashboard.focus.actions.decide'),
      targetUrl: '/approvals',
    })),
    ...hotLeads.map((lead) => ({
      id: lead.deal.id,
      title: lead.deal.title,
      subtitle: lead.deal.contactName
        ? `${lead.deal.contactName} · ${translateHotLeadReason(lead.primaryReason, t)}`
        : translateHotLeadReason(lead.primaryReason, t),
      status: 'active' as const,
      priority: (lead.score >= 80 ? 'P0' : 'P1') as FocusPriority,
      actionLabel: t('crm.dashboard.focus.actions.view_deal'),
      targetUrl: `/deals/${lead.deal.id}`,
      prompt: t('crm.dashboard.focus.prompts.deal_recovery', { dealTitle: lead.deal.title }),
      amount: Number(lead.deal.amount),
      currency: lead.deal.currency,
      lastActivityAt: dealById.get(lead.deal.id)?.lastActivityAt ?? null,
    })),
    ...(tasks ?? [])
      .filter(
        (task) =>
          task.status !== 'done' &&
          task.status !== 'cancelled' &&
          task.dueAt &&
          new Date(task.dueAt).getTime() < nowTime,
      )
      .slice(0, 3)
      .map((task) => ({
        id: `task-${task.id}`,
        title: task.title,
        subtitle: task.deal?.title
          ? `${task.deal.title} · ${t('crm.dashboard.focus.overdue_task')}`
          : t('crm.dashboard.focus.overdue_task'),
        status: 'blocked' as const,
        priority: 'P0' as const,
        actionLabel: t('crm.dashboard.focus.actions.view_task'),
        targetUrl: `/tasks/${task.id}`,
      })),
    ...deals
      .filter(
        (deal) =>
          deal.stage !== 'Won' &&
          deal.stage !== 'Lost' &&
          !deal.nextActivityDue &&
          !hotLeads.some((l) => l.deal.id === deal.id),
      )
      .slice(0, 3)
      .map((deal) => {
        const contactName = deal.contact
          ? [deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(' ')
          : undefined;
        return {
          id: deal.id,
          title: deal.title,
          subtitle: contactName
            ? `${contactName} · ${t('crm.dashboard.focus.no_next_step')}`
            : t('crm.dashboard.focus.no_next_step'),
          status: 'todo' as const,
          priority: 'P1' as const,
          actionLabel: t('crm.dashboard.focus.actions.plan_step'),
          targetUrl: `/deals/${deal.id}`,
          prompt: t('crm.dashboard.focus.prompts.plan_step', { dealTitle: deal.title }),
          amount: Number(deal.amount),
          currency: deal.currency,
          lastActivityAt: deal.lastActivityAt,
        };
      }),
  ];

  const workforce: readonly WorkforceAgent[] = [
    ...signals.scenarioRuns.map((run, index) => ({
      id: run.id,
      name:
        index === 0
          ? i18n.t('crm.features.features.dashboard.dashboard.vm.напоминание_о_визите')
          : index === 1
            ? i18n.t('crm.features.features.dashboard.dashboard.vm.сбор_отзыва_whatsapp')
            : i18n.t('crm.features.features.dashboard.dashboard.vm.реактивация_спящих'),
      status: (run.status === 'running' ? 'running' : 'offline') as 'running' | 'offline',
      type: 'agent' as const,
      currentProject: index === 0 ? run.scenarioId : run.id,
    })),
    ...signals.pendingApprovals.map((approval) => {
      const kindLabelKey = `crm.dashboard.notifications.approval.kind.${approval.kind}`;
      const kindLabel = t(kindLabelKey);
      // t returns the raw key when the kind is unknown — fall back to the raw kind.
      return {
        id: approval.id,
        name: i18n.t('crm.features.features.dashboard.dashboard.vm.перенос_записи_конфликт'),
        status: (approval.status === 'pending' ? 'running' : 'offline') as 'running' | 'offline',
        type: 'agent' as const,
        currentProject: kindLabel === kindLabelKey ? approval.kind : kindLabel,
      };
    }),
  ];

  const burndownSource = deals
    .map((deal) => deal.lastActivityAt ?? deal.nextActivityDue)
    .filter((value): value is string => value !== null)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  const burndownData: readonly BurndownPoint[] = burndownSource.map((timestamp, index) => ({
    date: new Date(timestamp).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
    ideal: Math.max(burndownSource.length - index - 1, 0),
    remaining: Math.max(activeDealsCount - index, 0),
  }));

  const salonStages = [
    { source: 'New', target: i18n.t('crm.features.features.dashboard.dashboard.vm.новая_запись') },
    {
      source: 'Contacted',
      target: i18n.t('crm.features.features.dashboard.dashboard.vm.требует_подтверждения'),
    },
    {
      source: 'Negotiation',
      target: i18n.t('crm.features.features.dashboard.dashboard.vm.переговоры'),
    },
    {
      source: 'Won',
      target: i18n.t('crm.features.features.dashboard.dashboard.vm.услуга_оказана'),
    },
  ];

  const pipelines: readonly PipelineItem[] = salonStages.map(({ source, target }) => {
    const count = deals.filter((deal) => deal.stage === source).length;
    return {
      id: target,
      name: `${target} · ${count}`,
      status: source === 'Won' ? 'completed' : 'running',
      count,
    };
  });

  // ── Funnel mini-kanban (spec D `.pipe-cols`): three stage groups, fresh
  // first, up to three cards per column. Lost deals stay out of the funnel.
  const freshFirst = [...deals].sort((a, b) => {
    const aTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
    const bTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
    return bTime - aTime;
  });
  const pipelineCards: readonly PipelineBoardCard[] = freshFirst
    .filter((deal) => deal.stage !== 'Lost')
    .map((deal) => ({
      id: deal.id,
      title: deal.title,
      contact: deal.contact
        ? [deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(' ') || null
        : null,
      amount: Number(deal.amount),
      currency: deal.currency,
      stage: deal.stage,
      nextActivityDue: deal.nextActivityDue,
    }));

  const activityItems: readonly RecentActivityItem[] =
    activities.length > 0
      ? activities.slice(0, 4).map((activity) => {
          const kindKey = `crm.dashboard.activity.kind.${activity.kind}`;
          const translated = t(kindKey);
          // t returns the raw key when the kind is unknown — fall back to the
          // generic label instead of printing an internal snake_case kind.
          const kindLabel =
            translated === kindKey ? t('crm.dashboard.activity.fallback_title') : translated;
          const contactName = activity.contact
            ? [activity.contact.firstName, activity.contact.lastName].filter(Boolean).join(' ')
            : null;
          const rawSubject = activity.subject?.trim() ?? '';
          // Some producers store an entity handle ("crm:<id>") in the subject.
          // It is an internal identifier, never a thing the user recognises, so
          // it must not reach the widget.
          const readableSubject = ENTITY_HANDLE_PATTERN.test(rawSubject) ? null : rawSubject;
          const entityName = activity.deal?.title ?? contactName ?? readableSubject ?? null;
          const scopeType = activity.kind.startsWith('deal_')
            ? 'pipeline'
            : activity.kind.startsWith('task_')
              ? 'task'
              : activity.kind.startsWith('scenario_') || activity.kind === 'ai_tool_invoked'
                ? 'session'
                : 'system';
          return {
            id: activity.id,
            ...(activity.deal?.id ? { dealId: activity.deal.id } : {}),
            title: kindLabel,
            ...(entityName ? { message: entityName } : {}),
            timestamp: activity.occurredAt,
            scopeType,
          };
        })
      : deals.slice(0, 4).map((deal) => ({
          id: deal.id,
          dealId: deal.id,
          title: deal.title,
          message: deal.contact
            ? [deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(' ')
            : deal.stage,
          timestamp: deal.lastActivityAt ?? deal.nextActivityDue ?? new Date().toISOString(),
          scopeType: 'pipeline',
        }));

  const deadlines: readonly CalendarDeadline[] = deals
    .filter(
      (deal): deal is DealListItem & { nextActivityDue: string } => deal.nextActivityDue !== null,
    )
    .slice(0, 3)
    .map((deal) => ({
      id: deal.id,
      title: deal.title,
      date: deal.nextActivityDue,
      project: deal.contact?.firstName ?? deal.title,
    }));

  const notifications: readonly NotificationItem[] = [
    ...signals.pendingApprovals.map((approval) => {
      const kindLabelKey = `crm.dashboard.notifications.approval.kind.${approval.kind}`;
      const kindLabel = t(kindLabelKey);
      return {
        id: approval.id,
        title: t('crm.dashboard.notifications.approval.title'),
        body: t('crm.dashboard.notifications.approval.body', {
          kind: kindLabel === kindLabelKey ? approval.kind : kindLabel,
        }),
        type: 'approval' as const,
        read: false,
        createdAt: approval.createdAt,
        targetUrl: '/approvals',
        ...(approval.entityLabel === undefined
          ? {}
          : {
              entityLabel: approval.entityLabel,
              ...(approval.entityKind === undefined ? {} : { entityKind: approval.entityKind }),
            }),
      };
    }),
    ...hotLeads.slice(0, 2).map((lead) => ({
      id: `hot-${lead.deal.id}`,
      dealId: lead.deal.id,
      title: t('crm.dashboard.notifications.hot_lead.title', { title: lead.deal.title }),
      body: translateHotLeadReason(lead.primaryReason, t),
      type: 'warning' as const,
      read: false,
      createdAt:
        deals.find((deal) => deal.id === lead.deal.id)?.nextActivityDue ??
        deals.find((deal) => deal.id === lead.deal.id)?.lastActivityAt ??
        new Date().toISOString(),
    })),
  ].slice(0, 4);

  return {
    stats,
    focusItems,
    workforce,
    burndownData,
    pipelines,
    pipelineCards,
    activityItems,
    deadlines,
    notifications,
    onboarding,
  };
}
